import { useFiltersContext } from '@/context/FiltersContext/FiltersContextProvider';
import { AvailableValuesByFilter, OptionPathsByFilter } from '@/context/FiltersContext/FiltersContext';
import { FILTER_CONFIG, FilterType, FilterValue } from '@/models';
import { globalFilters as globalFiltersAllowlist, hcoGlobalFilters } from '@/models/filter/globalFilterAllowlist';
import { LocalOptionPathsByFilter, useLocalOptionPathsByFilter } from '@/models/filter/utils/localOptionPathsStore';
import { FilterState, isActiveFilter } from '@/types/filter';
import { valueToAvailabilityKey } from '@/utils/filter/cascadingFilters';
import { isStringArray } from '@/api/queries/utils';
import { Filter, LogicalAndFilter } from '@cubejs-client/core';
import { groupBy, isArray, omit } from 'lodash-es';
import { useMemo } from 'react';

const isFilterType = (key: string): key is FilterType => key in FILTER_CONFIG;

/**
 * Cascading filters: drop selected values that are no longer available (return
 * zero results) for the current filter combination so disabled values do not
 * affect the query. Values with no registered availability set are left as-is.
 */
const excludeDisabledValues = (
  key: FilterType,
  value: FilterValue,
  availableValuesByFilter: AvailableValuesByFilter,
): FilterValue => {
  const availableValues = availableValuesByFilter[key];
  // No registered availability set means options have not resolved yet — keep the
  // original selection rather than dropping values prematurely. Cascading filters
  // only use string[] selections, so other value shapes pass through untouched.
  if (!availableValues || !isStringArray(value)) return value;
  return value.filter(selectedValue => availableValues.has(valueToAvailabilityKey(selectedValue)));
};

const processFilter = (
  key: FilterType,
  value: FilterValue,
  availableValuesByFilter: AvailableValuesByFilter,
  optionPathsByFilter: OptionPathsByFilter,
  localOptionPathsByFilter: LocalOptionPathsByFilter,
): Filter | null => {
  const config = FILTER_CONFIG[key];
  if (!isActiveFilter(config)) return null;
  const effectiveValue = excludeDisabledValues(key, value, availableValuesByFilter);
  if ((isArray(effectiveValue) && effectiveValue.length) || (!isArray(effectiveValue) && effectiveValue)) {
    const optionPaths = optionPathsByFilter[key] ?? localOptionPathsByFilter[key];
    return config.applyFilter(effectiveValue, { optionPaths }) ?? null;
  }
  return null;
};

const EMPTY_AVAILABLE_VALUES: AvailableValuesByFilter = {};
const EMPTY_OPTION_PATHS: OptionPathsByFilter = {};

interface BuildGlobalFiltersInputs {
  availableValuesByFilter: AvailableValuesByFilter;
  optionPathsByFilter: OptionPathsByFilter;
  localOptionPathsByFilter: LocalOptionPathsByFilter;
}

export const buildGlobalFilters = (
  state: Partial<FilterState>,
  { availableValuesByFilter, optionPathsByFilter, localOptionPathsByFilter }: BuildGlobalFiltersInputs,
): LogicalAndFilter => {
  const globalFilters: LogicalAndFilter = { and: [] };

  const groupedFilters = groupBy(
    Object.entries(state).filter((filterEntry): filterEntry is [FilterType, FilterValue] => {
      const [key, value] = filterEntry;
      if (!isFilterType(key)) return false;
      return !!value && isActiveFilter(FILTER_CONFIG[key]);
    }),
    ([key]) => {
      const config = FILTER_CONFIG[key];
      return isActiveFilter(config) ? (config.filterGroup ?? 'undefined') : 'undefined';
    },
  );
  Object.values(groupedFilters).forEach(filters => {
    filters.forEach(([key, value]) => {
      const filterExpression = processFilter(
        key,
        value,
        availableValuesByFilter,
        optionPathsByFilter,
        localOptionPathsByFilter,
      );
      if (filterExpression) globalFilters.and.push(filterExpression);
    });
  });
  return globalFilters;
};

export const useGetGlobalFilters = (
  overrideFilterState?: Partial<FilterState>,
  omittedFilters?: FilterType | FilterType[],
  disableFilterStateForComparisonSegment?: boolean,
  excludeDisabled = true,
): LogicalAndFilter => {
  const { filterState, availableValuesByFilter = {}, optionPathsByFilter = EMPTY_OPTION_PATHS } = useFiltersContext();
  const localOptionPathsByFilter = useLocalOptionPathsByFilter();

  // The disabled-value exclusion reads the availability registry that option
  // queries themselves populate. Feeding it back into an option query creates a
  // registry -> query -> options -> registry loop (React "Maximum update depth
  // exceeded"), so callers fetching options opt out and only data queries prune
  // disabled selections.
  const effectiveAvailableValues = excludeDisabled ? availableValuesByFilter : EMPTY_AVAILABLE_VALUES;

  return useMemo(() => {
    const availableFilterState = disableFilterStateForComparisonSegment ? {} : filterState;
    const state = omit(
      overrideFilterState ? { ...availableFilterState, ...overrideFilterState } : filterState,
      omittedFilters ?? [],
    );
    return buildGlobalFilters(state, {
      availableValuesByFilter: effectiveAvailableValues,
      optionPathsByFilter,
      localOptionPathsByFilter,
    });
  }, [
    filterState,
    omittedFilters,
    overrideFilterState,
    disableFilterStateForComparisonSegment,
    effectiveAvailableValues,
    optionPathsByFilter,
    localOptionPathsByFilter,
  ]);
};

interface AllowlistedGlobalFiltersOptions {
  // Set false for cubes that cannot join to hco (e.g. consent-overview cubes).
  includeHco?: boolean;
  excludeDisabled?: boolean;
}

export const useAllowlistedGlobalFilters = (
  { includeHco = true, excludeDisabled = true }: AllowlistedGlobalFiltersOptions = {},
  overrideFilterState?: Partial<FilterState>,
  disableFilterStateForComparisonSegment?: boolean,
): LogicalAndFilter => {
  const omitFilters = useMemo(() => {
    const applied = new Set<FilterType>([...globalFiltersAllowlist, ...(includeHco ? hcoGlobalFilters : [])]);
    return Object.values(FilterType).filter(filterType => !applied.has(filterType));
  }, [includeHco]);

  return useGetGlobalFilters(overrideFilterState, omitFilters, disableFilterStateForComparisonSegment, excludeDisabled);
};
